const API =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API_URL ||
  "https://api.bossdrop.ai";

async function request(path, { method = "GET", body, keepalive = false } = {}) {
  const response = await fetch(`${API}${path}`, {
    method,
    cache: "no-store",
    keepalive,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: body == null ? undefined : JSON.stringify(body),
  });
  const text = await response.text().catch(() => "");
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {}
  if (!response.ok) {
    // The message stays the server's code, so every `e.message` caller works
    // as before. The status and parsed body ride along (same shape as
    // helpers/auth.js) because some refusals carry more than a code: the
    // workspace doc's directory fence names the address it refused on
    // `.data.email`. Do not log the whole error object; log `.message`.
    const err = new Error(json?.error || json?.message || text || "request_failed");
    err.status = response.status;
    err.data = json;
    throw err;
  }
  return json || {};
}

/**
 * Authenticated JSON request against the configured API base
 * (REACT_APP_API_BASE / REACT_APP_API_URL, cookies included). Same contract as
 * the internal helper: resolves to the parsed body (or {}), and throws
 * Error(<server error code>) on a non-2xx response.
 */
export function apiRequest(path, options) {
  return request(path, options);
}

/** An authenticated file from the API: its Blob, or Error(<server error code>). */
export async function apiDownload(path) {
  const response = await fetch(`${API}${path}`, { cache: "no-store", credentials: "include" });
  if (!response.ok) {
    const json = await response.json().catch(() => null);
    const err = new Error(json?.error || "request_failed");
    err.status = response.status;
    throw err;
  }
  return response.blob();
}

export async function loadData(_email) {
  const json = await request("/api/workspace/doc");
  return json?.doc || null;
}

/**
 * The workspace document WITH the server's answer about it:
 * `{ doc, docPresent, ownerEmail }`.
 *
 * `loadData` above collapses three different answers into one value: a stored
 * document, a workspace that has nothing stored yet, and — before the server
 * stopped swallowing it — a read that failed. A caller that cannot tell them
 * apart eventually treats "I could not read your team" as "you have no team"
 * and writes that back, which is how a whole workspace's access map got
 * deleted. This one THROWS on a failed read (never resolves to an empty doc)
 * and reports `docPresent: false` only when the server affirmatively said the
 * workspace has no document.
 */
export async function loadWorkspaceDocument(_email) {
  const json = await request("/api/workspace/doc");
  const doc =
    json?.doc && typeof json.doc === "object" && !Array.isArray(json.doc)
      ? json.doc
      : null;
  return {
    doc,
    // A server older than this build does not send the flag. Infer it from the
    // document itself rather than assuming "present": a document with keys was
    // read, an empty one is treated as nothing stored, and the caller's rule
    // ("only mirror a directory the document actually carried") holds either way.
    docPresent:
      typeof json?.docPresent === "boolean"
        ? json.docPresent
        : !!(doc && Object.keys(doc).length > 0),
    ownerEmail: typeof json?.ownerEmail === "string" ? json.ownerEmail : "",
  };
}

export async function saveData(_email, data, options = {}) {
  const json = await request("/api/workspace/doc", {
    method: "PUT",
    body: { doc: data || {} },
    keepalive: !!options?.keepalive,
  });
  return json?.doc || data || {};
}

export async function deleteData(_email) {
  const json = await request("/api/workspace/doc", { method: "DELETE" });
  return json || { ok: true };
}

function arrayBufferToBase64(buffer) {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return window.btoa(binary);
}

export async function prepareWorkspaceContextFile(file) {
  if (!file) throw new Error("context_file_missing");
  const buffer = await file.arrayBuffer();
  const json = await request("/api/workspace/context-files/prepare", {
    method: "POST",
    body: {
      fileName: file.name || "context-file",
      mimeType: file.type || "",
      sizeBytes: Number(file.size) || buffer.byteLength || 0,
      dataBase64: arrayBufferToBase64(buffer),
    },
  });
  return json?.file || null;
}
